import { useTranslation } from "react-i18next";
import {
  Activity,
  Calendar,
  ChevronLeft,
  ChevronRight,
  LineChart,
  User,
  Lock,
  SlidersHorizontal,
  CreditCard,
  Eye,
  StickyNote,
} from "@/components/icons";
import { NavItem } from "@/components/shell/NavItem";
import { UserPill } from "./UserPill";
import { useSidebarCollapsed } from "./useSidebarCollapsed";
import { useLanguage } from "@/i18n/useLanguage";
import { cn } from "@/lib/utils";

export function Sidebar() {
  const [collapsed, toggle] = useSidebarCollapsed();
  const { t } = useTranslation("common");
  // C5: directional chevrons must point inward toward the panel they open.
  // In RTL the sidebar lives on the right edge, so "expand" points left
  // (toward the page content) and "collapse" points right (toward the
  // sidebar edge). Swap the icon components rather than rotating so the
  // chevron's arrow tip lines up with the same intended action.
  const { rtl } = useLanguage();
  const ExpandIcon = rtl ? ChevronLeft : ChevronRight;
  const CollapseIcon = rtl ? ChevronRight : ChevronLeft;
  return (
    <aside
      className={cn(
        "h-full bg-card border-r border-border flex flex-col transition-[width] duration-200",
        collapsed ? "w-14" : "w-64",
      )}
      aria-expanded={!collapsed}
    >
      <div
        className={cn(
          "p-4 flex items-center gap-2",
          collapsed && "justify-center px-2",
        )}
      >
        <Activity className="h-5 w-5 text-primary shrink-0" />
        {!collapsed && (
          <span className="font-semibold tracking-tight">{t("nav.appName")}</span>
        )}
      </div>
      <nav className={cn("flex-1 space-y-1", collapsed ? "px-2" : "p-3")}>
        <NavItem to="/dashboard" icon={LineChart} label={t("nav.dashboard")} collapsed={collapsed} />
        <NavItem to="/monitors" icon={Eye} label={t("nav.monitors")} collapsed={collapsed} />
        <NavItem to="/calendar" icon={Calendar} label={t("nav.calendar")} collapsed={collapsed} />
        <NavItem to="/notes" icon={StickyNote} label={t("nav.notes")} collapsed={collapsed} />
        <NavItem to="/profile" icon={User} label={t("nav.profile")} collapsed={collapsed} />
        <NavItem to="/account" icon={Lock} label={t("nav.account")} end collapsed={collapsed} />
        <NavItem to="/preferences" icon={SlidersHorizontal} label={t("nav.preferences")} collapsed={collapsed} />
        <NavItem to="/account/billing" icon={CreditCard} label={t("nav.billing")} collapsed={collapsed} />
      </nav>
      <button
        type="button"
        onClick={toggle}
        aria-label={collapsed ? t("sidebar.expand") : t("sidebar.collapse")}
        className={cn(
          "mx-2 mb-2 flex items-center justify-center rounded-md py-1.5 text-muted-foreground",
          "hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
        )}
      >
        {collapsed ? (
          <ExpandIcon className="h-4 w-4" data-testid="sidebar-toggle-icon" />
        ) : (
          <CollapseIcon className="h-4 w-4" data-testid="sidebar-toggle-icon" />
        )}
      </button>
      <div className={cn("border-t border-border", collapsed ? "p-2" : "p-3")}>
        <UserPill collapsed={collapsed} />
      </div>
    </aside>
  );
}
